'use client'

/**
 * The two chevrons that step a slider, at whatever size the caller needs.
 *
 * Shared by the features slider's nav and the floating review bar, so the two
 * are the same control rather than two drawings of it. The arrows are the app's
 * own chevrons, not lucide's: heavier, and cut to the same 24-box as the rest
 * of the drawn icons here.
 */

/** How far the glyph sits from the right edge of its own viewBox. */
const GLYPH_RIGHT_GAP = 7.29
const VIEWBOX = 24

const PREVIOUS = 'M15.29 20L7.28999 12L15.29 4L16.71 5.42L10.13 12L16.71 18.58L15.29 20Z'
const NEXT = 'M8.70999 4L16.71 12L8.70999 20L7.28999 18.58L13.87 12L7.28999 5.42L8.70999 4Z'

/**
 * What to pull the pair right by so the chevron LOOKS flush with a margin
 * rather than measuring flush: its own whitespace, plus the gap between the
 * svg and the edge of its tap target. At 32/36 that is 11.72px.
 */
export function opticalRightInset(glyph: number, target: number): number {
  return (GLYPH_RIGHT_GAP / VIEWBOX) * glyph + (target - glyph) / 2
}

export function SliderArrows({
  current,
  total,
  onPrevious,
  onNext,
  previousLabel,
  nextLabel,
  /** Size of the chevron. */
  glyph = 32,
  /** Size of the tap target around it. */
  target = 36,
  gap = 12,
  className = '',
}: {
  current: number
  total: number
  onPrevious: () => void
  onNext: () => void
  previousLabel: string
  nextLabel: string
  glyph?: number
  target?: number
  gap?: number
  className?: string
}) {
  const atStart = current === 0
  const atEnd = current === total - 1

  const arrow = (
    d: string,
    label: string,
    onClick: () => void,
    disabled: boolean
  ) => (
    <button
      onClick={onClick}
      disabled={disabled}
      style={{ width: target, height: target }}
      className={`flex items-center justify-center ${
        disabled ? 'text-gray-400 cursor-default' : 'cursor-pointer'
      }`}
      aria-label={label}
    >
      <svg
        width={glyph}
        height={glyph}
        viewBox="0 0 24 24"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path d={d} fill="currentColor" />
      </svg>
    </button>
  )

  return (
    <div
      style={{ gap, marginRight: -opticalRightInset(glyph, target) }}
      className={`flex items-center ${className}`}
    >
      {arrow(PREVIOUS, previousLabel, onPrevious, atStart)}
      {arrow(NEXT, nextLabel, onNext, atEnd)}
    </div>
  )
}
